<template>
  <header
    :class="[
      'fixed top-0 left-0 right-0 z-50 transition-all duration-500',
      isScrolled
        ? 'bg-white shadow-sm border-b border-navy-100'
        : 'bg-cream/80 backdrop-blur-sm'
    ]"
  >
    <nav class="container-app">
      <div class="flex items-center justify-between h-16 md:h-20">
        <!-- Brand logo -->
        <a href="#inicio" class="flex items-center group" aria-label="APS — Expertos en pensiones">
          <img src="/aps.png" alt="APS Expertos en Pensiones" class="h-11 md:h-14 w-auto" />
        </a>

        <!-- Desktop nav -->
        <div class="hidden lg:flex items-center space-x-1">
          <template v-for="link in navLinks">
            <!-- Partners: dropdown con socios -->
            <div v-if="link.id === 'socios'" :key="link.hash" class="relative group">
              <a
                :href="link.hash"
                :class="[
                  'inline-flex items-center gap-1.5 px-5 py-2.5 text-base font-semibold transition-all duration-200',
                  activeSection === link.id
                    ? 'text-primary-600 border-b-2 border-primary-500'
                    : 'text-navy-700 hover:text-primary-600'
                ]"
                aria-haspopup="true"
              >
                {{ link.label }}
                <svg class="w-4 h-4 transition-transform duration-200 group-hover:rotate-180" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
                </svg>
              </a>

              <!-- Dropdown -->
              <div class="absolute top-full left-1/2 -translate-x-1/2 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200">
                <div class="w-80 bg-white rounded-2xl shadow-2xl border border-navy-100 overflow-hidden">
                  <div class="px-4 pt-3 pb-2">
                    <p class="text-[10px] font-bold uppercase tracking-[0.18em] text-navy-400">Nuestros socios</p>
                  </div>
                  <ul class="py-1">
                    <li v-for="p in partners" :key="p.name">
                      <a :href="p.href || null" :target="p.href ? '_blank' : null" :rel="p.href ? 'noopener noreferrer' : null"
                         class="flex items-center gap-3 px-4 py-2.5 hover:bg-primary-50 transition-colors group/item">
                        <span class="w-10 h-10 rounded-lg bg-white border border-navy-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          <img :src="`/${p.file}`" :alt="p.name" class="max-h-7 max-w-[80%] object-contain" />
                        </span>
                        <span class="flex-1 min-w-0">
                          <span class="block text-sm font-semibold text-navy-700 group-hover/item:text-primary-600 leading-tight">{{ p.service }}</span>
                          <span class="block text-[11px] text-navy-400 mt-0.5">{{ p.name }}</span>
                        </span>
                        <svg v-if="p.href" class="w-4 h-4 text-navy-300 group-hover/item:text-primary-500 group-hover/item:translate-x-0.5 transition-all" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                        </svg>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Link normal -->
            <a v-else
              :key="link.hash"
              :href="link.hash"
              :class="[
                'px-5 py-2.5 text-base font-semibold  transition-all duration-200',
                activeSection === link.id
                  ? 'text-primary-600 border-b-2 border-primary-500'
                  : 'text-navy-700 hover:text-primary-600'
              ]"
            >
              {{ link.label }}
            </a>
          </template>

          <!-- Redes sociales (desktop) -->
          <span class="hidden xl:inline-block w-px h-6 bg-navy-200 mx-3"></span>
          <div class="hidden xl:flex items-center gap-1">
            <a v-for="r in redes" :key="r.label"
               :href="r.href" target="_blank" rel="noopener noreferrer"
               :aria-label="r.label"
               class="w-9 h-9 flex items-center justify-center text-navy-500 hover:text-primary-600 hover:bg-navy-50 rounded-full transition-colors duration-200">
              <span v-html="r.icon"></span>
            </a>
          </div>

          <a
            href="https://wa.me/5215513408411?text=Hola%20APS%2C%20quiero%20una%20asesor%C3%ADa%20sobre%20mi%20pensi%C3%B3n."
            target="_blank"
            rel="noopener noreferrer"
            class="ml-3 px-5 py-3 text-base font-bold bg-navy-900 text-white rounded-full hover:bg-navy-800 transition-colors duration-200 shadow-aps whitespace-nowrap"
          >
            Solicita tu asesoría
          </a>
        </div>

        <!-- Mobile button -->
        <button
          class="lg:hidden p-2 transition-colors duration-200 hover:bg-navy-50"
          @click="isMobileMenuOpen = !isMobileMenuOpen"
          aria-label="Abrir menú"
        >
          <svg :class="['w-7 h-7', isScrolled ? 'text-primary-700' : 'text-white']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path v-if="!isMobileMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </nav>

    <!-- Mobile menu -->
    <transition name="slide-down">
      <div v-if="isMobileMenuOpen" class="lg:hidden bg-white/98 backdrop-blur-xl shadow-xl border-t border-primary-100 max-h-[calc(100vh-4rem)] overflow-y-auto">
        <div class="px-6 py-6 space-y-1">
          <template v-for="link in navLinks">
            <!-- Partners: expandible -->
            <div v-if="link.id === 'socios'" :key="link.hash">
              <button
                type="button"
                class="w-full flex items-center justify-between px-4 py-3 text-base font-medium rounded-xl text-neutral-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-200"
                @click="mobilePartnersOpen = !mobilePartnersOpen"
              >
                <span>{{ link.label }}</span>
                <svg :class="['w-4 h-4 transition-transform', mobilePartnersOpen && 'rotate-180']" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>
              <div v-if="mobilePartnersOpen" class="ml-1 pl-2 border-l-2 border-primary-200 my-1 space-y-0.5">
                <a v-for="p in partners" :key="p.name"
                   :href="p.href || null" :target="p.href ? '_blank' : null" :rel="p.href ? 'noopener noreferrer' : null"
                   class="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-primary-50 transition-colors"
                   @click="isMobileMenuOpen = false">
                  <span class="w-9 h-9 rounded-lg bg-white border border-navy-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <img :src="`/${p.file}`" :alt="p.name" class="max-h-6 max-w-[80%] object-contain" />
                  </span>
                  <span class="flex-1 min-w-0">
                    <span class="block text-sm font-semibold text-navy-700 leading-tight break-words">{{ p.service }}</span>
                    <span class="block text-[11px] text-navy-400 mt-0.5">{{ p.name }}</span>
                  </span>
                  <svg v-if="p.href" class="w-3.5 h-3.5 text-navy-300 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                  </svg>
                </a>
              </div>
            </div>

            <a v-else
              :key="link.hash"
              :href="link.hash"
              class="block px-4 py-3 text-base font-medium rounded-xl text-neutral-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-200"
              @click="isMobileMenuOpen = false"
            >
              {{ link.label }}
            </a>
          </template>
          <!-- Redes sociales (móvil) -->
          <div class="flex items-center justify-center gap-3 mt-4 pt-4 border-t border-primary-100">
            <a v-for="r in redes" :key="r.label"
               :href="r.href" target="_blank" rel="noopener noreferrer"
               :aria-label="r.label"
               class="w-10 h-10 flex items-center justify-center rounded-full text-navy-600 bg-navy-50 hover:bg-primary-500 hover:text-white transition-colors duration-200"
               @click="isMobileMenuOpen = false">
              <span v-html="r.icon"></span>
            </a>
          </div>

          <a
            href="https://wa.me/5215513408411?text=Hola%20APS%2C%20quiero%20una%20asesor%C3%ADa%20sobre%20mi%20pensi%C3%B3n."
            target="_blank"
            rel="noopener noreferrer"
            class="block text-center mt-3 px-4 py-3 text-base font-bold bg-navy-900 text-white rounded-xl hover:bg-navy-800 transition-colors duration-200"
            @click="isMobileMenuOpen = false"
          >
            Solicita tu asesoría
          </a>
        </div>
      </div>
    </transition>
  </header>
</template>

<script>
import { PARTNERS } from '@/data/partners';

const ICON_TIKTOK = '<svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>';
const ICON_FB = '<svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>';
const ICON_IG = '<svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>';
const ICON_LI = '<svg class="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>';

export default {
  name: 'TheHeader',
  data() {
    return {
      isMobileMenuOpen: false,
      mobilePartnersOpen: false,
      isScrolled: false,
      activeSection: 'inicio',
      partners: PARTNERS,
      navLinks: [
        { label: 'Inicio',      hash: '#inicio',      id: 'inicio'      },
        { label: 'Servicios',   hash: '#servicios',   id: 'servicios'   },
        { label: 'Nosotros',    hash: '#nosotros',    id: 'nosotros'    },
        { label: 'Proceso',     hash: '#proceso',     id: 'proceso'     },
        { label: 'Contacto',    hash: '#contacto',    id: 'contacto'    },
        { label: 'Partners',    hash: '#socios',      id: 'socios'      },
      ],
      redes: [
        { label: 'TikTok',    href: 'https://www.tiktok.com/@yanelifigueroa_aps',                              icon: ICON_TIKTOK },
        { label: 'Facebook',  href: 'https://www.facebook.com/share/14dbx19ikjh/',                              icon: ICON_FB },
        { label: 'Instagram', href: 'https://www.instagram.com/yaneli.aps/',                                    icon: ICON_IG },
        { label: 'LinkedIn',  href: 'https://www.linkedin.com/in/aps-experto-enpensiones-12774b410/',           icon: ICON_LI },
      ]
    };
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    onScroll() {
      this.isScrolled = window.scrollY > 60;
      const sections = ['socios', 'contacto', 'proceso', 'nosotros', 'calculadora', 'servicios'];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 150) {
          this.activeSection = id;
          return;
        }
      }
      this.activeSection = 'inicio';
    }
  }
};
</script>
